import {
  EMERGENCY_VISIT_NOTES,
  HOSPITALIZATIONS_COMPLICATIONS,
  HOSPITALIZATIONS_FOLLOW_UP,
} from '@constants/extension-codes';

import { formatFhirDate, getFhirDate } from '@utils/fhir';
import { formatAddress, formatDate } from '@utils/format';

import type {
  EncounterModel,
  EncounterStatus,
  EncounterModelInput,
} from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';
import { LocationRecord } from './location';

export type EncounterValues = Pick<
  EncounterRecord,
  | 'city'
  | 'complications'
  | 'country'
  | 'diagnosis'
  | 'endDate'
  | 'erNotes'
  | 'facility'
  | 'followUp'
  | 'practitioner'
  | 'selfReported'
  | 'startDate'
  | 'visitDurationDays'
  | 'visitDurationUnits'
> & {
  // visitReason
  title: string;
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & EncounterValues;

type EncounterType = EncounterModel;
export class EncounterRecord extends FhirResource {
  protected model: EncounterType;

  public readonly location: LocationRecord | undefined;

  public constructor(model: EncounterType) {
    super(model);
    this.model = model;
    const locations = this.model.locationResource;
    if (locations?.length) {
      const [location] = locations;
      this.location = new LocationRecord(location);
    }
  }

  public get type() {
    const { code } = this.model.class;
    return code;
  }

  public get visitReason() {
    const { reasonCode } = this.model;
    return reasonCode?.map(({ text }) => text).join('/');
  }

  public set visitReason(value: string | null | undefined) {
    this.model = {
      ...this.model,
      reasonCode: value ? [{ text: value }] : null,
    };
  }

  public get facility() {
    const locations = this.model.locationResource;
    if (locations?.length) {
      const [location] = locations;
      return location.name;
    }
    return null;
  }

  public get address() {
    const locations = this.model.locationResource;
    if (locations?.length) {
      const [location] = locations;
      const { address: _a } = location;
      return formatAddress(
        _a?.line,
        _a?.city,
        _a?.postalCode,
        _a?.state,
        _a?.country
      );
    }
    return null;
  }

  public get city() {
    const locations = this.model.locationResource;
    if (locations?.length) {
      const [location] = locations;
      const { address: _a } = location;
      return _a?.city;
    }
    return null;
  }

  public get country() {
    const locations = this.model.locationResource;
    if (locations?.length) {
      const [location] = locations;
      const { address: _a } = location;
      return _a?.country;
    }
    return null;
  }

  public get practitioner() {
    return this.model.participant
      ?.filter(
        ({ individual }) =>
          individual?.reference?.startsWith('Practitioner') ||
          !individual?.reference
      )
      .map(({ individual }) => individual?.display)
      .join('/');
  }

  public set practitioner(value: string | null | undefined) {
    this.model = {
      ...this.model,
      participant: value
        ? [
            {
              individual: {
                display: value,
              },
              type: [
                { coding: [{ code: 'practitioner' }], text: 'Practitioner' },
              ],
            },
          ]
        : null,
    };
  }

  /**
   * (Hospitalizations Only) Visit Duration (e.g. 8 days)
   */
  public get visitDuration() {
    const { length } = this.model;
    if (length?.value) {
      return `${length.value} ${length.unit ? `${length.unit}` : ''}`;
    }
    return null;
  }

  /**
   * (Hospitalizations Only) Visit Duration Days
   */
  public get visitDurationDays() {
    const { length } = this.model;
    return length?.value;
  }

  public set visitDurationDays(value: number | null | undefined) {
    this.model = {
      ...this.model,
      length: value ? { value: Number(value) } : undefined,
    };
  }

  /**
   * (Hospitalizations Only) Visit Duration Unit
   */
  public get visitDurationUnits() {
    const { length } = this.model;
    return length?.unit;
  }

  public set visitDurationUnits(value: string | null | undefined) {
    this.model = {
      ...this.model,
      length: value
        ? {
            value: Number(this.visitDurationDays),
            unit: value,
          }
        : null,
    };
  }

  public get diagnosis() {
    return this.model.diagnosis
      ?.map(({ condition }) => condition.display)
      .join('/');
  }

  public set diagnosis(value: string | null | undefined) {
    this.model = {
      ...this.model,
      diagnosis: value
        ? [
            {
              condition: {
                display: value,
              },
            },
          ]
        : null,
    };
  }

  public get followUp() {
    const extension = this.findExtension(HOSPITALIZATIONS_FOLLOW_UP);
    return extension?.valueString;
  }

  public set followUp(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: HOSPITALIZATIONS_FOLLOW_UP,
        valueString: value,
      });
    } else {
      this.removeExtension(HOSPITALIZATIONS_FOLLOW_UP);
    }
  }

  /**
   * (Hospitalizations Only) Complications
   */
  public get complications() {
    const extension = this.findExtension(HOSPITALIZATIONS_COMPLICATIONS);
    return extension?.valueString;
  }

  public set complications(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: HOSPITALIZATIONS_COMPLICATIONS,
        valueString: value,
      });
    } else {
      this.removeExtension(HOSPITALIZATIONS_COMPLICATIONS);
    }
  }

  /**
   * (Emergency Visits Only) Notes
   */
  public get erNotes() {
    const extension = this.findExtension(EMERGENCY_VISIT_NOTES);
    return extension?.valueString;
  }

  public set erNotes(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: EMERGENCY_VISIT_NOTES,
        valueString: value,
      });
    } else {
      this.removeExtension(EMERGENCY_VISIT_NOTES);
    }
  }

  public get startDate() {
    return this.model.period?.start;
  }

  public set startDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      period: {
        start: value,
        end: this.endDate,
      },
    };
  }

  public get endDate() {
    return this.model.period?.end;
  }

  public set endDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      period: {
        end: value,
      },
    };
  }

  public get visitDate() {
    const { period } = this.model;
    const start = period?.start ? formatDate({ date: period.start }) : '';
    const end = period?.end ? formatDate({ date: period.end }) : '';
    return `${start}${end ? ` - ${end}` : ''}`;
  }

  public getVisitDate() {
    const { period } = this.model;
    const start = period?.start;
    return getFhirDate(start);
  }

  public getVisitDateStr(options?: {
    includeEnd?: boolean;
    showTime?: boolean;
  }) {
    const includeEnd = options?.includeEnd ?? true;
    const showTime = options?.showTime ?? false;

    const { period } = this.model;
    const start = period?.start
      ? formatFhirDate(period.start, { showTime })
      : '';
    const end = period?.end ? formatFhirDate(period.end, { showTime }) : '';
    return `${start}${end && includeEnd ? ` - ${end}` : ''}`;
  }

  public getModelInput(
    values?: Omit<EncounterValues, 'city' | 'country' | 'facility'>
  ): EncounterModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.visitReason = values.title;
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    delete cleaned.locationResource;
    return {
      ...cleaned,
      status: cleaned.status as EncounterStatus,
    };
  }
}
